import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { errorMessage } from '../lib/auth';
import { useQuery } from '../lib/useQuery';
import { useUnread } from '../lib/unread';
import { resolveNotificationLink } from '../lib/notificationLinks';
import { dateTime } from '../lib/labels';

/**
 * The inbox.
 *
 * Two things here were broken in ways that made the whole screen ornamental,
 * and both are worth naming because the shape of each is easy to reintroduce.
 *
 *   · unread was read off `sentAt`, which for an in-app notification is
 *     stamped the moment it is queued — the row IS the delivery for that
 *     channel. So everything arrived already marked read. It is `readAt` now,
 *     which is a separate column meaning a separate thing.
 *
 *   · the stored link was navigated to directly, and none of the paths the
 *     server wrote were routes this app declares. Every tap reached the 404
 *     screen. Paths are resolved now, with a fallback that lands somewhere
 *     useful rather than somewhere broken.
 */

interface Note {
  id: string;
  eventType: string;
  subject: string | null;
  body: string;
  linkPath: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  readAt: string | null;
}

export function Notifications() {
  const nav = useNavigate();
  const unread = useUnread();
  const notes = useQuery<Note[]>(() => api.get('/me/notifications?limit=50'), []);
  const [markingAll, setMarkingAll] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const unreadHere = notes.data?.filter((n) => !n.readAt).length ?? 0;

  async function open(n: Note) {
    const to = resolveNotificationLink(n);

    if (!n.readAt) {
      // Optimistic, and safe to be: this is a read receipt, not clinical state.
      // A worker who taps a notification has read it whether or not the write
      // lands, and holding the row unread until the network answers would make
      // a 3G tap feel like it did nothing.
      unread.setCount(Math.max(unread.count - 1, 0));
      api.post(`/me/notifications/${n.id}/read`)
        .then(() => { if (!to) notes.reload(); })
        .catch(() => {
          // Put it back. The badge is the only place the failure shows, and a
          // count that drifts down on every failed tap is worse than one that
          // corrects itself.
          unread.refresh();
        });
    }

    if (to) nav(to);
    else notes.reload();
  }

  async function markAll() {
    setMarkingAll(true);
    setActionError(null);
    try {
      await api.post('/me/notifications/read-all');
      notes.reload();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      // Either way the badge is re-read from the server rather than assumed:
      // a partial failure must not leave the count claiming zero.
      unread.refresh();
      setMarkingAll(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-base font-semibold text-neutral-900">Notifications</h1>
        {unreadHere > 0 && (
          <button
            onClick={markAll}
            disabled={markingAll}
            className="-mr-2 min-h-11 shrink-0 rounded-lg px-2 text-sm font-medium text-primary-700 underline underline-offset-2
                       active:bg-neutral-100 disabled:text-neutral-500 disabled:no-underline"
          >
            {markingAll ? 'Marking…' : 'Mark all read'}
          </button>
        )}
      </div>

      {actionError && (
        <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">
          {actionError}
        </div>
      )}

      {notes.loading ? (
        <div role="status" aria-label="Loading" className="space-y-2">
          <div className="h-24 animate-pulse rounded-xl bg-neutral-100" />
          <div className="h-24 animate-pulse rounded-xl bg-neutral-100" />
        </div>
      ) : notes.error ? (
        <div className="space-y-3">
          <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">
            {notes.error}
          </div>
          <button
            onClick={notes.reload}
            className="min-h-12 w-full rounded-lg border border-neutral-300 bg-white text-sm font-medium text-neutral-800 active:bg-neutral-100"
          >
            Try again
          </button>
        </div>
      ) : !notes.data?.length ? (
        <div className="rounded-xl border border-dashed border-neutral-300 px-5 py-10 text-center">
          <p className="text-sm font-medium text-neutral-800">No notifications</p>
          <p className="mt-1 text-sm text-neutral-600">
            You will get a message here when your appointment is booked, your results are ready or
            your certificate is issued.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {notes.data.map((n) => (
            <li key={n.id}>
              <button
                onClick={() => open(n)}
                className={`w-full rounded-xl border p-4 text-left active:bg-neutral-50 ${
                  n.readAt ? 'border-neutral-200 bg-white' : 'border-primary-200 bg-primary-50'
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className={`text-sm text-neutral-900 ${n.readAt ? 'font-medium' : 'font-semibold'}`}>
                    {n.subject ?? 'Update'}
                  </p>
                  {/* Not colour alone: unread is said in words, and also
                      changes the weight, the border and the ground. An
                      aria-label on a bare span was never read out. */}
                  {!n.readAt && (
                    <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary-700">
                      <span aria-hidden className="h-2 w-2 rounded-full bg-primary-600" />
                      New
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-neutral-700">{n.body}</p>
                <p className="mt-1.5 text-xs text-neutral-600">{dateTime(n.createdAt)}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
